/**
 * AccountIdentifierRotation — PNI/ACI-equivalent account-identifier rotation.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/AccountIdentifierRotation.swift`.
 *
 * Signal lets a user change their phone number without losing E2EE state: the
 * public-facing identifier rotates, but the underlying identity key and active
 * sessions are preserved. Peers learn about the rename via a signed notice
 * rather than seeing a "key changed!" warning.
 *
 * Kaether's `userId` plays the role of Signal's discoverable handle. When it
 * rotates we need to:
 *
 *   1. Keep the identity key, registration id, and signed prekey untouched.
 *   2. Re-key every local artefact filed under the old userId:
 *        - SignalProtocolStore session records    (e2ee:session:<uid>.<dev>)
 *        - SignalProtocolStore trusted-identity   (e2ee:trusted:<uid>)
 *        - IdentityVerificationStore records
 *        - KeyAuditor history
 *   3. Tell peers with a *signed* notice binding (oldUserId → newUserId) to
 *      the identity key they already trust. Without that signature a hostile
 *      server could silently rebind any userId to another.
 *
 * Forward-secrecy invariant: this module never touches identity-key material
 * or ratchet state. It is pure index renaming. To also rotate identity keys,
 * use `E2EEManager.resetKeys()` — a separate, more expensive flow.
 *
 * Wire-format interop: the canonical signed-bytes encoding and the JSON
 * `AccountRotationNotice` shape are byte-identical to the iOS implementation,
 * so a notice produced on iOS can be verified on web and vice versa.
 *
 * No SolidJS imports. No server calls. Pure crypto + local-state migration.
 */

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export type AccountIdentifierRotationErrorKind =
  | "sameUserId"
  | "emptyUserId"
  | "missingTrustedIdentityForOldUserId"
  | "signatureVerificationFailed"
  | "noticeExpired"
  | "noticeFromFuture"
  | "unsupportedNoticeVersion"
  | "malformedNotice";

export class AccountIdentifierRotationError extends Error {
  constructor(
    public readonly kind: AccountIdentifierRotationErrorKind,
    public readonly extra?: number | string,
  ) {
    super(extra !== undefined ? `${kind}: ${extra}` : kind);
    this.name = "AccountIdentifierRotationError";
  }
}

// ---------------------------------------------------------------------------
// Wire format
// ---------------------------------------------------------------------------

/**
 * Signed assertion that the holder of the identity key trusted as `oldUserId`
 * is now reachable as `newUserId`. Sent peer-to-peer (or fanned out by the
 * server), but the signature is what authorises the rename.
 *
 * Wire format (JSON-serialisable — mirrors iOS exactly):
 * ```json
 * {
 *   "version": 1,
 *   "oldUserId": "alice@kaether.fr",
 *   "newUserId": "alice2@kaether.fr",
 *   "issuedAtMillis": 1715347200000,
 *   "signature": "<base64 Ed25519 signature>"
 * }
 * ```
 */
export interface AccountRotationNotice {
  readonly version: number;
  readonly oldUserId: string;
  readonly newUserId: string;
  /** Unix milliseconds. Freshness window: `[now − 30d, now + 5min]`. */
  readonly issuedAtMillis: number;
  /**
   * Ed25519 signature over `signedBytes(...)` by the identity key trusted as
   * belonging to `oldUserId`. Base64-encoded (standard, with padding).
   */
  readonly signature: string;
}

export const CURRENT_NOTICE_VERSION = 1;

/**
 * The ASCII unit-separator (0x1F) is the field delimiter. It cannot legally
 * appear inside a userId, so the canonical form is unambiguous without
 * escaping and matches the iOS implementation exactly.
 */
const SEP = "";

/**
 * Canonical bytes that the Ed25519 signature covers. Deterministic encoding so
 * signing and verification produce identical inputs across platforms.
 *
 * Format (UTF-8 bytes of): `v<version><SEP><oldUserId><SEP><newUserId><SEP><issuedAtMillis>`
 *
 * Example for version=1, old="alice@kaether.fr", new="alice2@kaether.fr", ts=1715347200000:
 *   `v1\x1Falice@kaether.fr\x1Falice2@kaether.fr\x1F1715347200000`
 */
export function signedBytes(
  version: number,
  oldUserId: string,
  newUserId: string,
  issuedAtMillis: number,
): Uint8Array {
  const canonical = `v${version}${SEP}${oldUserId}${SEP}${newUserId}${SEP}${issuedAtMillis}`;
  return new TextEncoder().encode(canonical);
}

// ---------------------------------------------------------------------------
// SubtleCrypto Ed25519 helpers
// ---------------------------------------------------------------------------
// NOTE: Ed25519 via SubtleCrypto is specified in the W3C WebCrypto spec and
// is available in Chrome 113+, Firefox 130+, Safari 17+, and Node 18+.
// The self-test environment (browser or vitest with Node) must therefore
// support it. No third-party crypto library is introduced.
//
// Chrome's W3C Ed25519 implementation refuses `importKey("raw", ..., ["sign"])`
// — only public keys can be imported as `"raw"`. Private 32-byte seeds must be
// wrapped in a minimal PKCS#8 DER blob and imported as `"pkcs8"`. The 16-byte
// header below is the constant ASN.1 prefix for an Ed25519 OneAsymmetricKey
// containing a 32-byte CurvePrivateKey OCTET STRING (RFC 8410 §7).

/** PKCS#8 header for a 32-byte Ed25519 seed (constant; total wrapped length = 48). */
const ED25519_PKCS8_PREFIX = new Uint8Array([
  0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06,
  0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20,
]);

function wrapEd25519Pkcs8(seed: Uint8Array): Uint8Array {
  const out = new Uint8Array(ED25519_PKCS8_PREFIX.length + seed.length);
  out.set(ED25519_PKCS8_PREFIX, 0);
  out.set(seed, ED25519_PKCS8_PREFIX.length);
  return out;
}

/**
 * Import a raw 32-byte Ed25519 private key seed for signing.
 * Wraps the seed in PKCS#8 because Chrome rejects `"raw"` private-key imports.
 */
async function importPrivateKey(rawBytes: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "pkcs8",
    wrapEd25519Pkcs8(rawBytes),
    { name: "Ed25519" },
    false,
    ["sign"],
  );
}

/**
 * Import a raw 32-byte Ed25519 public key for verification.
 */
async function importPublicKey(rawBytes: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "raw",
    rawBytes,
    { name: "Ed25519" },
    false,
    ["verify"],
  );
}

/** Encode a Uint8Array to standard Base64 with padding. */
function uint8ToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

/** Decode standard Base64 to Uint8Array. */
function base64ToUint8(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

// ---------------------------------------------------------------------------
// Notice construction helpers (encode / decode / sign / verify)
// ---------------------------------------------------------------------------

/**
 * Encode a notice to JSON bytes (UTF-8).
 */
export function encodeNotice(notice: AccountRotationNotice): Uint8Array {
  return new TextEncoder().encode(JSON.stringify(notice));
}

/**
 * Decode a notice from JSON bytes (UTF-8).
 * Throws a plain `SyntaxError` if the bytes are not valid JSON.
 */
export function decodeNotice(bytes: Uint8Array): AccountRotationNotice {
  const json = new TextDecoder().decode(bytes);
  return JSON.parse(json) as AccountRotationNotice;
}

/**
 * Sign an unsigned notice (all fields except `signature`) with the given
 * raw 32-byte Ed25519 private key. Returns the complete `AccountRotationNotice`.
 */
export async function signNotice(
  unsignedNotice: Omit<AccountRotationNotice, "signature">,
  privateKeyBytes: Uint8Array,
): Promise<AccountRotationNotice> {
  const bytes = signedBytes(
    unsignedNotice.version,
    unsignedNotice.oldUserId,
    unsignedNotice.newUserId,
    unsignedNotice.issuedAtMillis,
  );
  const privateKey = await importPrivateKey(privateKeyBytes);
  const sigBuffer = await crypto.subtle.sign("Ed25519", privateKey, bytes);
  return {
    ...unsignedNotice,
    signature: uint8ToBase64(new Uint8Array(sigBuffer)),
  };
}

/**
 * Verify an `AccountRotationNotice`'s Ed25519 signature against the supplied
 * raw 32-byte public key.
 *
 * Returns `true` if the signature is valid, `false` otherwise.
 * Does NOT check version or freshness — that is done in `verifyNotice`.
 */
export async function verifyNoticeSignature(
  notice: AccountRotationNotice,
  publicKeyBytes: Uint8Array,
): Promise<boolean> {
  const bytes = signedBytes(
    notice.version,
    notice.oldUserId,
    notice.newUserId,
    notice.issuedAtMillis,
  );
  let publicKey: CryptoKey;
  try {
    publicKey = await importPublicKey(publicKeyBytes);
  } catch {
    return false;
  }
  let sigBytes: Uint8Array;
  try {
    sigBytes = base64ToUint8(notice.signature);
  } catch {
    return false;
  }
  return crypto.subtle.verify("Ed25519", publicKey, sigBytes, bytes);
}

// ---------------------------------------------------------------------------
// Constants — freshness window (mirrors iOS defaults)
// ---------------------------------------------------------------------------

export const DEFAULT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
export const DEFAULT_MAX_FUTURE_SKEW_MS = 5 * 60 * 1000;    // 5 minutes

// ---------------------------------------------------------------------------
// Full notice verification (version + freshness + signature)
// ---------------------------------------------------------------------------

/**
 * Verify a notice end-to-end: version check, empty-userId guard, freshness
 * window, and Ed25519 signature. Throws `AccountIdentifierRotationError` for
 * any failure so the caller can distinguish failure reasons.
 *
 * Pure function (no I/O). Does NOT look up the trusted key from a store —
 * that is the caller's (or `applyPeerAccountRotation`'s) responsibility.
 *
 * @param notice - The notice to verify.
 * @param publicKeyBytes - Raw 32-byte Ed25519 public key to verify against.
 * @param nowMs - Current time in Unix ms (override for testing).
 * @param maxAgeMs - Maximum age in ms.
 * @param maxFutureSkewMs - Maximum future skew in ms.
 */
export async function verifyNotice(
  notice: AccountRotationNotice,
  publicKeyBytes: Uint8Array,
  nowMs: number = Date.now(),
  maxAgeMs: number = DEFAULT_MAX_AGE_MS,
  maxFutureSkewMs: number = DEFAULT_MAX_FUTURE_SKEW_MS,
): Promise<void> {
  if (notice.version !== CURRENT_NOTICE_VERSION) {
    throw new AccountIdentifierRotationError(
      "unsupportedNoticeVersion",
      notice.version,
    );
  }
  if (!notice.oldUserId || !notice.newUserId) {
    throw new AccountIdentifierRotationError("malformedNotice");
  }
  if (notice.oldUserId === notice.newUserId) {
    throw new AccountIdentifierRotationError("sameUserId");
  }

  const skewMs = notice.issuedAtMillis - nowMs;
  if (skewMs > maxFutureSkewMs) {
    throw new AccountIdentifierRotationError("noticeFromFuture");
  }
  const ageMs = nowMs - notice.issuedAtMillis;
  if (ageMs > maxAgeMs) {
    throw new AccountIdentifierRotationError("noticeExpired");
  }

  const valid = await verifyNoticeSignature(notice, publicKeyBytes);
  if (!valid) {
    throw new AccountIdentifierRotationError("signatureVerificationFailed");
  }
}

// ---------------------------------------------------------------------------
// Store interfaces (mirroring iOS protocol surface, testability-first)
// ---------------------------------------------------------------------------

/**
 * Subset of the SignalProtocolStore that `AccountIdentifierRotation` depends
 * on. Defined as an interface so the self-test can exercise the full migration
 * flow against an in-memory mock without touching IndexedDB.
 *
 * The real `SignalProtocolStore` fulfils this contract. The migration code
 * never calls any other method.
 */
export interface AccountSessionMigrationStore {
  /**
   * Return the encoded-address strings (`"<userId>.<deviceId>"`) for all
   * sessions currently stored under `userId`.
   */
  sessionAddresses(userId: string): Promise<string[]>;

  loadSession(encodedAddress: string): Promise<string | undefined>;
  storeSession(encodedAddress: string, record: string): Promise<void>;
  removeSession(encodedAddress: string): Promise<void>;

  /** Returns the trusted identity public key (raw Base64) or `undefined`. */
  getTrustedIdentity(userId: string): Promise<string | undefined>;
  storeTrustedIdentity(userId: string, publicKeyB64: string): Promise<void>;
  removeTrustedIdentity(userId: string): Promise<void>;
}

/**
 * Minimal interface for the IdentityVerificationStore that migration touches.
 * The production store (not yet landed) will implement this. The self-test
 * uses an in-memory mock.
 */
export interface IdentityVerificationMigrationStore {
  /**
   * Move the verification record from `oldUserId` to `newUserId`.
   * Returns `true` if a record existed and was moved, `false` otherwise.
   */
  migrate(oldUserId: string, newUserId: string): Promise<boolean>;
  /** For testing: get the raw record or `undefined`. */
  getRecord(userId: string): Promise<IdentityVerificationRecord | undefined>;
  /** For testing: set a record. */
  setRecord(userId: string, record: IdentityVerificationRecord): Promise<void>;
}

/**
 * Minimal identity verification record shape. State machine mirrors iOS:
 * `"tofu"` → first-time-use, `"verified"` → user explicitly confirmed safety
 * numbers, `"changed"` → key mismatch detected.
 */
export interface IdentityVerificationRecord {
  readonly userId: string;
  readonly identityPubKeyB64: string;
  readonly state: "tofu" | "verified" | "changed";
  readonly verifiedAtMs?: number;
  readonly changedAtMs?: number;
}

/**
 * Minimal interface for the KeyAuditor that migration touches.
 */
export interface KeyAuditMigrationStore {
  /**
   * Move all audit history rows from `oldUserId` to `newUserId`.
   * Returns `true` if any rows were moved.
   */
  migrate(oldUserId: string, newUserId: string): Promise<boolean>;
  /** For testing: get all history entries for a userId. */
  historyFor(userId: string): Promise<KeyAuditEntry[]>;
  /** For testing: append a raw entry. */
  appendEntry(entry: KeyAuditEntry): Promise<void>;
}

export interface KeyAuditEntry {
  userId: string;
  identityPubKeyB64: string;
  observedAtMs: number;
  source: string;
}

// ---------------------------------------------------------------------------
// Rotation log (replay defence + local audit trail)
// ---------------------------------------------------------------------------

export interface AccountRotationLogEntry {
  readonly oldUserId: string;
  readonly newUserId: string;
  readonly issuedAtMillis: number;
  readonly appliedAtMs: number;
  /**
   * Hex SHA-256 of the identity key whose Ed25519 signature authorised the
   * rename. Lets future audits cross-check against the KeyAuditor history.
   */
  readonly authorisingKeyHashHex: string;
  /** `"self"` for our own rotation; `"peer"` for an incoming signed notice. */
  readonly kind: "self" | "peer";
}

/**
 * Interface for the rotation log. The default implementation is in-memory
 * (suitable for tests and the initial web deployment). A persistent variant
 * backed by IndexedDB can drop in later without changing the migration engine.
 */
export interface AccountRotationLog {
  entries(): AccountRotationLogEntry[];
  contains(oldUserId: string, newUserId: string, issuedAtMillis: number): boolean;
  append(entry: AccountRotationLogEntry): void;
  reset(): void;
}

/**
 * Simple in-memory `AccountRotationLog`. Suitable for the self-test and as
 * the initial production implementation until a persistent IDB variant lands.
 */
export class InMemoryRotationLog implements AccountRotationLog {
  private _entries: AccountRotationLogEntry[] = [];

  entries(): AccountRotationLogEntry[] {
    return [...this._entries];
  }

  contains(oldUserId: string, newUserId: string, issuedAtMillis: number): boolean {
    return this._entries.some(
      (e) =>
        e.oldUserId === oldUserId &&
        e.newUserId === newUserId &&
        e.issuedAtMillis === issuedAtMillis,
    );
  }

  append(entry: AccountRotationLogEntry): void {
    this._entries.push(entry);
  }

  reset(): void {
    this._entries = [];
  }
}

// ---------------------------------------------------------------------------
// Migration result
// ---------------------------------------------------------------------------

/**
 * Outcome of a single rotation apply (peer or self).
 *
 * `replay` is `true` iff the notice was already in the rotation log; in that
 * case all counts are 0/false and no migration ran.
 */
export interface MigrationResult {
  readonly sessionsMigrated: number;
  readonly identityRecordsMigrated: boolean;
  readonly keyAuditEntriesMigrated: boolean;
  /**
   * `true` iff the trusted-identity pin for `oldUserId` was moved to
   * `newUserId`.
   */
  readonly trustedIdentityMoved: boolean;
  /** `true` iff this notice was a replay of an already-applied rotation. */
  readonly replay: boolean;
}

// ---------------------------------------------------------------------------
// Utility: SHA-256 hex (for the authorising key hash in log entries)
// ---------------------------------------------------------------------------

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ---------------------------------------------------------------------------
// Migration core
// ---------------------------------------------------------------------------

/**
 * Walk every artefact filed under `oldUserId` and re-file it under `newUserId`.
 *
 * Stores migrated:
 *   1. SignalProtocolStore session records   — address re-keyed, ratchet bytes unchanged
 *   2. SignalProtocolStore trusted-identity  — single Base64 key string moved
 *   3. IdentityVerificationStore record      — state (tofu/verified/changed) preserved
 *   4. KeyAuditor history                   — full timeline preserved
 *
 * What is NOT touched:
 *   - Identity key, prekeys (that's the whole point)
 *   - Plaintext cache (keyed by ciphertext hash, not userId)
 *   - MessagePlaintextStore (keyed by messageId, not userId)
 *
 * Idempotent per-artefact: if `newUserId` already has an artefact, it is
 * overwritten with the value from `oldUserId` (last-write-wins). The audit
 * log specifically concatenates old + existing-new to preserve chronological
 * order.
 */
export async function migrateLocalState(
  oldUserId: string,
  newUserId: string,
  store: AccountSessionMigrationStore,
  verificationStore: IdentityVerificationMigrationStore,
  keyAuditStore: KeyAuditMigrationStore,
): Promise<MigrationResult> {
  // 1. Sessions: read each, rewrite at new address, delete old.
  const oldAddresses = await store.sessionAddresses(oldUserId);
  let sessionsMigrated = 0;
  for (const oldAddr of oldAddresses) {
    const record = await store.loadSession(oldAddr);
    if (record === undefined) continue;
    // Address format: "<userId>.<deviceId>" — split off the userId prefix.
    const dotIdx = oldAddr.indexOf(".");
    const deviceSuffix = dotIdx >= 0 ? oldAddr.slice(dotIdx) : "";
    const newAddr = `${newUserId}${deviceSuffix}`;
    await store.storeSession(newAddr, record);
    await store.removeSession(oldAddr);
    sessionsMigrated++;
  }

  // 2. Trusted-identity pin.
  let trustedIdentityMoved = false;
  const trusted = await store.getTrustedIdentity(oldUserId);
  if (trusted !== undefined) {
    await store.storeTrustedIdentity(newUserId, trusted);
    await store.removeTrustedIdentity(oldUserId);
    trustedIdentityMoved = true;
  }

  // 3. Verification record.
  const identityRecordsMigrated = await verificationStore.migrate(oldUserId, newUserId);

  // 4. Key-audit log.
  const keyAuditEntriesMigrated = await keyAuditStore.migrate(oldUserId, newUserId);

  return {
    sessionsMigrated,
    identityRecordsMigrated,
    keyAuditEntriesMigrated,
    trustedIdentityMoved,
    replay: false,
  };
}

// ---------------------------------------------------------------------------
// Owner-side: rotate our own account identifier
// ---------------------------------------------------------------------------

/**
 * Rotate *our* public account identifier from `oldUserId` to `newUserId`.
 *
 * What this does:
 *   - Validates inputs.
 *   - Builds and signs an `AccountRotationNotice` using the local identity key.
 *   - Applies `migrateLocalState` to move our own local artefacts.
 *   - Appends a `kind="self"` entry to the rotation log.
 *
 * What this does NOT do (caller's responsibility):
 *   - Fan out the notice to peers.
 *   - Update `E2EEManager.currentUserId`.
 *   - Show any UI.
 *
 * @returns The signed `AccountRotationNotice` (caller must ship it to peers)
 *          and the `MigrationResult` for local state.
 */
export async function rotateOwnAccountIdentifier(
  oldUserId: string,
  newUserId: string,
  privateKeyBytes: Uint8Array,
  publicKeyBytes: Uint8Array,
  stores: {
    store: AccountSessionMigrationStore;
    verificationStore: IdentityVerificationMigrationStore;
    keyAuditStore: KeyAuditMigrationStore;
    rotationLog: AccountRotationLog;
  },
  nowMs: number = Date.now(),
): Promise<{ notice: AccountRotationNotice; migrationResult: MigrationResult }> {
  if (!oldUserId || !newUserId) {
    throw new AccountIdentifierRotationError("emptyUserId");
  }
  if (oldUserId === newUserId) {
    throw new AccountIdentifierRotationError("sameUserId");
  }

  const unsigned: Omit<AccountRotationNotice, "signature"> = {
    version: CURRENT_NOTICE_VERSION,
    oldUserId,
    newUserId,
    issuedAtMillis: nowMs,
  };
  const notice = await signNotice(unsigned, privateKeyBytes);

  const migrationResult = await migrateLocalState(
    oldUserId,
    newUserId,
    stores.store,
    stores.verificationStore,
    stores.keyAuditStore,
  );

  const authorisingKeyHashHex = await sha256Hex(publicKeyBytes);
  stores.rotationLog.append({
    oldUserId,
    newUserId,
    issuedAtMillis: nowMs,
    appliedAtMs: nowMs,
    authorisingKeyHashHex,
    kind: "self",
  });

  return { notice, migrationResult };
}

// ---------------------------------------------------------------------------
// Peer-side: apply an incoming rotation notice
// ---------------------------------------------------------------------------

/**
 * Apply a peer's signed rotation notice to local state.
 *
 * Order of operations (mirrors iOS `applyPeerRotation`):
 *   1. Look up the identity key currently trusted for `notice.oldUserId`. A
 *      missing pin means we never trusted that peer — bail without migrating
 *      (prevents server from TOFU-pinning arbitrary identifier→key bindings).
 *   2. Verify the notice's signature against that pinned key.
 *   3. Replay-check against the local rotation log. If already applied, return
 *      `replay: true` with zero counts (no double-migration).
 *   4. Migrate every artefact filed under `oldUserId` → `newUserId`.
 *   5. Append a `kind="peer"` log entry.
 *
 * @param notice - The rotation notice received from the peer.
 * @param stores - The local stores to migrate.
 * @param nowMs - Current time in Unix ms (override for testing).
 * @param maxAgeMs - Maximum notice age in ms.
 * @param maxFutureSkewMs - Maximum future skew in ms.
 * @returns `MigrationResult` describing what was moved.
 * @throws `AccountIdentifierRotationError` if the notice is invalid.
 */
export async function applyPeerAccountRotation(
  notice: AccountRotationNotice,
  stores: {
    store: AccountSessionMigrationStore;
    verificationStore: IdentityVerificationMigrationStore;
    keyAuditStore: KeyAuditMigrationStore;
    rotationLog: AccountRotationLog;
  },
  nowMs: number = Date.now(),
  maxAgeMs: number = DEFAULT_MAX_AGE_MS,
  maxFutureSkewMs: number = DEFAULT_MAX_FUTURE_SKEW_MS,
): Promise<MigrationResult> {
  // Step 1: require a pre-existing trusted pin.
  const trustedKeyB64 = await stores.store.getTrustedIdentity(notice.oldUserId);
  if (trustedKeyB64 === undefined) {
    throw new AccountIdentifierRotationError("missingTrustedIdentityForOldUserId");
  }
  const trustedKeyBytes = base64ToUint8(trustedKeyB64);

  // Step 2: full notice verification (version, freshness, signature).
  await verifyNotice(notice, trustedKeyBytes, nowMs, maxAgeMs, maxFutureSkewMs);

  // Step 3: replay check.
  if (stores.rotationLog.contains(notice.oldUserId, notice.newUserId, notice.issuedAtMillis)) {
    return {
      sessionsMigrated: 0,
      identityRecordsMigrated: false,
      keyAuditEntriesMigrated: false,
      trustedIdentityMoved: false,
      replay: true,
    };
  }

  // Step 4: migrate local state.
  const result = await migrateLocalState(
    notice.oldUserId,
    notice.newUserId,
    stores.store,
    stores.verificationStore,
    stores.keyAuditStore,
  );

  // Step 5: append log entry.
  const authorisingKeyHashHex = await sha256Hex(trustedKeyBytes);
  stores.rotationLog.append({
    oldUserId: notice.oldUserId,
    newUserId: notice.newUserId,
    issuedAtMillis: notice.issuedAtMillis,
    appliedAtMs: nowMs,
    authorisingKeyHashHex,
    kind: "peer",
  });

  return result;
}
